
import React from 'react';
import ResellerStatCard from '@/components/superadmin/reseller/ResellerStatCard';
import ResellerAccountUsage from '@/components/superadmin/reseller/ResellerAccountUsage';
import { Users, DollarSign, ChevronsUpDown } from 'lucide-react';
import { ResellerPlanDetails } from '@/hooks/license/useResellerPlans';
import { useMasterLicenseSettings } from '@/contexts/MasterLicenseSettingsContext';

interface ResellerPlanOverviewProps {
  currentPlan: ResellerPlanDetails;
  currentRevenueData: any;
}

const ResellerPlanOverview: React.FC<ResellerPlanOverviewProps> = ({
  currentPlan,
  currentRevenueData
}) => {
  const { licenseSettings } = useMasterLicenseSettings();

  // Find the base revenue split tier (lowest threshold)
  const baseSplitTier = licenseSettings.revenueSplits?.length 
    ? [...licenseSettings.revenueSplits]
        .filter(split => split.isActive)
        .sort((a, b) => a.volumeThreshold - b.volumeThreshold)[0]
    : null;

  // Default to 30% reseller share if no splits are configured
  const baseResellerPercentage = baseSplitTier?.resellerPercentage || 30;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <ResellerStatCard
        title="Reseller Plan"
        bgClass="bg-gradient-to-br from-blue-50 to-indigo-50"
        borderClass="border border-blue-100"
        titleColor="text-blue-700"
        value={currentPlan.name}
        description={currentPlan.description}
        icon={<Users className="h-4 w-4 text-blue-600 mr-2" />}
        details={[
          `Up to ${currentPlan.maxAdmins} admin accounts`,
          `Up to ${currentPlan.maxUsers} user accounts`
        ]}
      />
      
      <ResellerStatCard
        title="Setup Fee"
        bgClass="bg-gradient-to-br from-green-50 to-emerald-50"
        borderClass="border border-green-100"
        titleColor="text-green-700"
        value={`$${currentPlan.setupFee}`}
        description="One-time setup fee"
        icon={<DollarSign className="h-4 w-4 text-green-600 mr-2" />}
        details={[
          `Base Revenue Share: ${baseResellerPercentage}%`,
          `Your Share: $${currentRevenueData?.resellerAmount.toLocaleString() || '0'}`
        ]}
      />
      
      <ResellerStatCard
        title="Revenue Tiers"
        bgClass="bg-gradient-to-br from-purple-50 to-violet-50"
        borderClass="border border-purple-100"
        titleColor="text-purple-700"
        value={licenseSettings.revenueSplits?.filter(s => s.isActive).length || 1}
        description="Active revenue split tiers"
        icon={<ChevronsUpDown className="h-4 w-4 text-purple-600 mr-2" />}
        details={[
          "Higher volume = better rates",
          "Synced with Master License"
        ]}
      />
    </div>
  );
};

export default ResellerPlanOverview;
